import React, { useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash-es';
import { Row, Col } from 'reactstrap';
/* Models */
import { InventoryEntities } from 'client/data/models/inventory';
import { VisitorModel } from 'client/data/models/visitor';
/* Constants */
import { TrackingConstant } from 'client/tracking/constant';
import { TOOLTIPS_IDS } from 'site-modules/shared/components/inventory/ev-insights/ev-insights-constants';
/* Utils */
import { EventToolbox } from 'client/utils/event-toolbox';
import { isNew } from 'site-modules/shared/utils/inventory-utils/is-new';
import {
  getMilesValue,
  getLowestIncr,
  MIN_MILE_VALUE,
} from 'site-modules/shared/utils/inventory/get-closest-range-value';
import { toShortFormattedAddress } from 'site-modules/shared/utils/address-format';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { handleTooltipOpen } from 'site-modules/shared/components/inventory/ev-insights/utils/ev-insights';
/* Components */
import { EVRangeMapImage } from 'site-modules/shared/components/ev-range-map-image/ev-range-map-image';
import { ZipWithDefaultActionHandling } from 'site-modules/shared/components/zip-with-default-action-handling/zip-with-default-action-handling';
import { TooltipItem } from 'site-modules/shared/components/tooltip-item/tooltip-item';

import './ev-range-map.scss';

const CREATIVE_ID = 'battery-range-and-charging';
const RECURRENT_RANGE_KEY = 'recurrent';
const EPA_RANGE_KEY = 'epa_range';
const RANGE_COPY_MAP = {
  [RECURRENT_RANGE_KEY]:
    'This map is a visual representation of the possible one-way and round-trips by this vehicle (on a full charge) from the geometric center of {location}. The depicted ranges are based on the estimated range provided by Recurrent, rounded down to {oneWay} miles for one-way and {roundTrip} miles for round-trip. Estimated range is based on Recurrent’s estimated battery range of this vehicle model. Actual range will vary depending on the condition of this vehicle’s battery pack, how you drive, driving conditions and other factors.',
  [`${RECURRENT_RANGE_KEY}_25`]:
    'This map is a visual representation of the possible one-way trips by this vehicle (on a full charge) from the geometric center of {location}. The depicted range is based on the minimum estimated range value provided by Recurrent, rounded down to 25 miles. Estimated range is based on Recurrent’s estimated battery health of this vehicle model and vehicles in similar model classes. Actual range will vary depending on the condition of this vehicle’s battery pack, how you drive, driving conditions and other factors.',
  [EPA_RANGE_KEY]:
    'This map is a visual representation of the possible one-way and round-trips by this vehicle (on a full charge) from the geometric center of {location}. The depicted ranges are based on the estimated new vehicle range value provided by the EPA, rounded down to {oneWay} miles for one-way and {roundTrip} miles for round-trip. Actual range will vary depending on the condition of this vehicle’s battery pack, how you drive, driving conditions and other factors.',
  [`${EPA_RANGE_KEY}_25`]:
    'This map is a visual representation of the possible one-way trips by this vehicle (on a full charge) from the geometric center of {location}. The depicted range is based on the estimated new vehicle range value provided by the EPA, rounded down to 25 miles. Actual range will vary depending on the condition of this vehicle’s battery pack, how you drive, driving conditions and other factors.',
};

function getRangeCopyMapKey({ miles, isNewVehicle, expectedRangeMin }) {
  let rangeCopyMapKey = EPA_RANGE_KEY;

  if (!isNewVehicle && expectedRangeMin) {
    rangeCopyMapKey = RECURRENT_RANGE_KEY;
  }

  if (miles === MIN_MILE_VALUE) {
    rangeCopyMapKey = `${rangeCopyMapKey}_25`;
  }

  return rangeCopyMapKey;
}

export function EvRangeMapUI({ children, vinData, visitorLocation = {} }) {
  const expectedRangeMin = get(vinData, 'thirdPartyInfo.recurrentInsights.expectedRange.min');
  const isNewVehicle = isNew(get(vinData, 'type'));
  const miles = getMilesValue({ vinData });
  const roundTrip = getLowestIncr(Math.floor(miles / 2));
  const rangeCopyMapKey = getRangeCopyMapKey({ miles, isNewVehicle, expectedRangeMin });
  const rangeCopy = RANGE_COPY_MAP[rangeCopyMapKey]
    .replace('{location}', toShortFormattedAddress(visitorLocation))
    .replace('{oneWay}', miles.toString())
    .replace('{roundTrip}', roundTrip.toString());

  useEffect(() => {
    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
      event_data: {
        action_name: TrackingConstant.ACTION_SHOW_CONTENT,
        action_cause: TrackingConstant.PAGE_LOAD_CAUSE,
        action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
        subaction_name: TrackingConstant.SUBACTION_SHOW_EV_RANGE_MAP,
        creative_id: CREATIVE_ID,
        value: miles,
      },
    });

    /* eslint-disable react-hooks/exhaustive-deps */
  }, []);

  return (
    <div className="ev-range-map">
      <Row className="align-items-start">
        <Col xs={12} lg={6} className="mb-1_5 mb-lg-0">
          {children}
        </Col>
        <Col xs={12} lg={6} className="pe-0 ps-0 ps-lg-1">
          <EVRangeMapImage miles={miles} />
          <div className="rounded-10 bg-cool-gray-100 py-1 px-0_5 mt-1 text-center">
            <Fragment>
              Estimated range map
              <TooltipItem
                className="top-0"
                item={{
                  placement: 'bottom',
                  text: (
                    <Fragment>
                      <div className="mb-1">{rangeCopy}</div>
                      Map display generated using: ©{' '}
                      <a
                        href="//openrouteservice.org/"
                        className="text-underline text-primary-darker"
                        rel="nofollow noopener noreferrer"
                        target="_blank"
                      >
                        openrouteservice.org
                      </a>{' '}
                      by HeiGIT | Map data ©{' '}
                      <a
                        href="//openstreetmap.org/"
                        className="text-underline text-primary-darker"
                        rel="nofollow noopener noreferrer"
                        target="_blank"
                      >
                        OpenStreetMap
                      </a>{' '}
                      contributors |{' '}
                      <a
                        href="//leafletjs.com/"
                        className="text-underline text-primary-darker"
                        rel="nofollow noopener noreferrer"
                        target="_blank"
                      >
                        Leaflet
                      </a>
                    </Fragment>
                  ),
                }}
                id={TOOLTIPS_IDS.ABOUT_MAP}
                delay={{ show: 0, hide: 250 }}
                autohide={false}
                onTooltipOpen={handleTooltipOpen}
              />{' '}
              from
            </Fragment>{' '}
            <ZipWithDefaultActionHandling buttonClassName="size-16 fw-normal" />
          </div>
        </Col>
      </Row>
    </div>
  );
}

EvRangeMapUI.propTypes = {
  children: PropTypes.node.isRequired,
  vinData: InventoryEntities.InventoryVin.isRequired,
  visitorLocation: PropTypes.shape({
    city: PropTypes.string,
    stateCode: PropTypes.string,
  }),
};

export const stateToPropsConfig = {
  visitorLocation: bindToPath('location', VisitorModel),
};

export const EvRangeMap = connectToModel(EvRangeMapUI, stateToPropsConfig);
